import { normalizeRelativePath } from "./relativePath";

// 폴더 입력에서 업로드 대상 파일 목록을 스캔한다.
//
// Owns:
// - 두 입력 경로(input[webkitdirectory] File 목록, 드래그앤드롭 DataTransfer 트리)를 공통
//   FolderScanResult 로 정규화하는 정책.
// - relative_path 계산 규칙: **선택한 폴더 자체가 데이터셋 루트**다(워커 PROBING 이 루트에서
//   meta/info.json 등 v1 레이아웃을 기대한다). 그래서 webkitRelativePath 의 첫 세그먼트(루트 폴더명)를 제거한다.
// - OS 잡파일 필터, 정규화 실패·중복 경로 거부. 거부된 파일은 declare 에 절대 넣지 않는다
//   (한 건이라도 invalid 면 서버가 배치 전체를 422 로 거부한다).
//
// Does not own:
// - 경로 문자열 정규화 규칙 자체(relativePath.ts).
// - 스캔 결과의 업로드 실행(UploadEngine).

export type ScannedFile = {
  file: File;
  relativePath: string;
  sizeBytes: number;
};

export type RejectedFileReason = "invalid_path" | "duplicate_path" | "os_junk";

export type RejectedFile = {
  rawPath: string;
  reason: RejectedFileReason;
};

export type FolderScanResult = {
  // relativePath 정규화와 중복 제거를 마친 목록. 사전순으로 정렬한다(선언·업로드 순서를 정한다).
  files: ScannedFile[];
  rejected: RejectedFile[];
  totalBytes: number;
  // 선택한 루트 폴더명. 데이터셋 display_name 추론에 쓴다. 알 수 없으면(개별 파일 입력) null.
  rootName: string | null;
};

// 드래그앤드롭 스캔 결과. 폴더 1개 드롭만 지원한다(파일이나 여러 루트는 문구와 함께 거부한다).
export type DropScanResult =
  | { kind: "ok"; scan: FolderScanResult }
  | { kind: "not_folder" }
  | { kind: "multiple_roots" };

// macOS/Windows 가 폴더에 자동으로 만드는 잡파일. 데이터셋 내용이 아니므로 알림 없이 거른다.
const OS_JUNK_FILE_NAMES = new Set([".DS_Store", "Thumbs.db", "desktop.ini"]);

// input[webkitdirectory] 선택(또는 이미 평탄화된 File 목록)을 스캔한다.
// webkitRelativePath("root/sub/f.ext")가 있으면 루트 세그먼트를 제거하고, 없으면 파일명을 쓴다.
export function scanFileList(files: readonly File[]): FolderScanResult {
  const rootName = inferRootName(files);
  const entries = files.map((file) => ({
    file,
    rawPath: stripRootSegment(file.webkitRelativePath) || file.name,
  }));
  return buildScanResult(entries, rootName);
}

// 드롭된 DataTransferItem 목록을 스캔한다. 루트가 폴더 1개일 때만 트리를 재귀 순회한다.
// 엔트리 fullPath("/root/sub/f.ext")에서 루트 폴더 접두사를 제거해 relative path 를 만든다.
export async function scanDroppedItems(
  items: ArrayLike<DataTransferItem>,
): Promise<DropScanResult> {
  const entries = collectDroppedEntries(items);
  if (entries.length !== 1) {
    return entries.length === 0 ? { kind: "not_folder" } : { kind: "multiple_roots" };
  }
  const root = entries[0];
  if (!root.isDirectory) {
    return { kind: "not_folder" };
  }
  const rootDirectory = root as FileSystemDirectoryEntry;
  const collected: { file: File; rawPath: string }[] = [];
  await collectDirectoryFiles(rootDirectory, rootDirectory.fullPath, collected);
  return { kind: "ok", scan: buildScanResult(collected, rootDirectory.name) };
}

function collectDroppedEntries(items: ArrayLike<DataTransferItem>): FileSystemEntry[] {
  const entries: FileSystemEntry[] = [];
  for (let index = 0; index < items.length; index += 1) {
    const entry = items[index].webkitGetAsEntry();
    if (entry !== null) {
      entries.push(entry);
    }
  }
  return entries;
}

async function collectDirectoryFiles(
  directory: FileSystemDirectoryEntry,
  rootPrefix: string,
  collected: { file: File; rawPath: string }[],
): Promise<void> {
  for (const entry of await readAllDirectoryEntries(directory)) {
    if (entry.isDirectory) {
      await collectDirectoryFiles(entry as FileSystemDirectoryEntry, rootPrefix, collected);
      continue;
    }
    if (entry.isFile) {
      const file = await readEntryFile(entry as FileSystemFileEntry);
      // "/root/sub/f.ext" → "sub/f.ext" (루트 폴더 = 데이터셋 루트).
      collected.push({ file, rawPath: stripPrefix(entry.fullPath, rootPrefix) });
    }
  }
}

// readEntries 는 한 번에 일부(Chromium 은 100개)만 반환하므로 빈 배열이 올 때까지 반복한다.
async function readAllDirectoryEntries(
  directory: FileSystemDirectoryEntry,
): Promise<FileSystemEntry[]> {
  const reader = directory.createReader();
  const all: FileSystemEntry[] = [];
  for (;;) {
    const batch = await new Promise<FileSystemEntry[]>((resolve, reject) => {
      reader.readEntries(resolve, reject);
    });
    if (batch.length === 0) {
      return all;
    }
    all.push(...batch);
  }
}

function readEntryFile(entry: FileSystemFileEntry): Promise<File> {
  return new Promise<File>((resolve, reject) => {
    entry.file(resolve, reject);
  });
}

function buildScanResult(
  entries: readonly { file: File; rawPath: string }[],
  rootName: string | null,
): FolderScanResult {
  const files: ScannedFile[] = [];
  const rejected: RejectedFile[] = [];
  const seenPaths = new Set<string>();
  for (const { file, rawPath } of entries) {
    if (OS_JUNK_FILE_NAMES.has(file.name)) {
      rejected.push({ rawPath, reason: "os_junk" });
      continue;
    }
    const normalized = normalizeRelativePath(rawPath);
    if (!normalized.ok) {
      rejected.push({ rawPath, reason: "invalid_path" });
      continue;
    }
    if (seenPaths.has(normalized.path)) {
      rejected.push({ rawPath, reason: "duplicate_path" });
      continue;
    }
    seenPaths.add(normalized.path);
    files.push({ file, relativePath: normalized.path, sizeBytes: file.size });
  }
  files.sort((left, right) => (left.relativePath < right.relativePath ? -1 : 1));
  return {
    files,
    rejected,
    totalBytes: files.reduce((sum, item) => sum + item.sizeBytes, 0),
    rootName,
  };
}

function inferRootName(files: readonly File[]): string | null {
  for (const file of files) {
    const path = file.webkitRelativePath;
    if (path !== "") {
      return path.split("/")[0] || null;
    }
  }
  return null;
}

function stripRootSegment(webkitRelativePath: string): string {
  if (webkitRelativePath === "") {
    return "";
  }
  const separatorIndex = webkitRelativePath.indexOf("/");
  return separatorIndex === -1 ? webkitRelativePath : webkitRelativePath.slice(separatorIndex + 1);
}

function stripPrefix(fullPath: string, prefix: string): string {
  return fullPath.startsWith(prefix + "/") ? fullPath.slice(prefix.length + 1) : fullPath;
}
